import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, CheckSquare, Plane, Plus, Edit, Trash2 } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const AssistantWorkManagementPage = () => {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  const [assistant, setAssistant] = useState(null);
  const [activeTab, setActiveTab] = useState('tasks'); // tasks, calendar, travel
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [travelItems, setTravelItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { success, error: showError } = useToast();

  useEffect(() => {
    if (assistantId) {
      fetchAssistantData();
      fetchTasks();
      fetchEvents();
      fetchTravel();
    }
  }, [assistantId]);

  const fetchAssistantData = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        const foundAssistant = data.assistants?.find(a => a.id === assistantId);
        setAssistant(foundAssistant);
      }
    } catch (err) {
      console.error('Error fetching assistant:', err);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/tasks`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/calendar`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchTravel = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantId}/travel`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setTravelItems(data.travel_items || []);
      }
    } catch (err) {
      console.error('Error fetching travel:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'todo': '#9CA3AF',
      'in_progress': '#3B82F6',
      'completed': '#10B981',
      'blocked': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#6B7280',
      'medium': '#F59E0B',
      'high': '#EF4444'
    };
    return colors[priority] || '#6B7280';
  };

  if (loading && !assistant) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', background: '#F8F9FA' }}>
        <p style={{ color: '#6B7280' }}>Loading...</p>
      </div>
    );
  }

  if (!assistant) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', background: '#F8F9FA' }}>
        <p style={{ color: '#6B7280' }}>Assistant not found</p>
        <button
          onClick={() => navigate('/admin/assistants')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: '#3B82F6',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Back to Assistants
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <button
              onClick={() => navigate('/admin/assistants')}
              style={{
                padding: '8px',
                background: 'transparent',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ArrowLeft size={18} color="#6B7280" />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
                {assistant.full_name}'s Work
              </h1>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                Manage tasks, calendar events, and travel for {assistant.email}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            <button
              onClick={() => setActiveTab('tasks')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'tasks' ? '#3B82F6' : 'transparent',
                color: activeTab === 'tasks' ? '#FFFFFF' : '#6B7280',
                border: activeTab === 'tasks' ? 'none' : '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <CheckSquare size={16} />
              Tasks ({tasks.length})
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'calendar' ? '#3B82F6' : 'transparent',
                color: activeTab === 'calendar' ? '#FFFFFF' : '#6B7280',
                border: activeTab === 'calendar' ? 'none' : '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Calendar size={16} />
              Calendar ({events.length})
            </button>
            <button
              onClick={() => setActiveTab('travel')}
              style={{
                padding: '10px 20px',
                background: activeTab === 'travel' ? '#3B82F6' : 'transparent',
                color: activeTab === 'travel' ? '#FFFFFF' : '#6B7280',
                border: activeTab === 'travel' ? 'none' : '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plane size={16} />
              Travel ({travelItems.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {tasks.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <CheckSquare size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: '#6B7280', fontSize: '14px' }}>No tasks found for this assistant</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Task</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Priority</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Due Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Executive</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, idx) => (
                    <tr key={task.id} style={{ borderBottom: idx < tasks.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding: '16px' }}>
                        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>{task.title}</p>
                        {task.description && (
                          <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>{task.description}</p>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: `${getPriorityColor(task.priority)}20`,
                          color: getPriorityColor(task.priority)
                        }}>
                          {task.priority || 'medium'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: `${getStatusColor(task.status)}20`,
                          color: getStatusColor(task.status)
                        }}>
                          {task.status?.replace('_', ' ') || 'todo'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#374151', fontSize: '13px' }}>
                          {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#6B7280', fontSize: '13px' }}>
                          {task.executive_name || 'Not assigned'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {events.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <Calendar size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: '#6B7280', fontSize: '14px' }}>No calendar events found for this assistant</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Event</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Date & Time</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, idx) => (
                    <tr key={event.id} style={{ borderBottom: idx < events.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding: '16px' }}>
                        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>{event.title}</p>
                        {event.description && (
                          <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>{event.description}</p>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#374151', fontSize: '13px' }}>{event.event_type || 'personal'}</span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <p style={{ color: '#374151', fontSize: '13px', margin: '0 0 2px 0' }}>
                          {event.start_datetime ? new Date(event.start_datetime).toLocaleString() : 'N/A'}
                        </p>
                        {event.end_datetime && (
                          <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                            to {new Date(event.end_datetime).toLocaleString()}
                          </p>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#6B7280', fontSize: '13px' }}>{event.location || 'No location'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Travel Tab */}
        {activeTab === 'travel' && (
          <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {travelItems.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <Plane size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: '#6B7280', fontSize: '14px' }}>No travel items found for this assistant</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Trip Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Travel Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Provider</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Confirmation</th>
                  </tr>
                </thead>
                <tbody>
                  {travelItems.map((item, idx) => (
                    <tr key={item.id} style={{ borderBottom: idx < travelItems.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding: '16px' }}>
                        <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: 0 }}>{item.trip_name}</p>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#374151', fontSize: '13px' }}>{item.type || 'N/A'}</span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#374151', fontSize: '13px' }}>
                          {item.travel_date ? new Date(item.travel_date).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#6B7280', fontSize: '13px' }}>{item.provider || 'N/A'}</span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: '#6B7280', fontSize: '13px', fontFamily: 'monospace' }}>
                          {item.confirmation_number || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantWorkManagementPage;
